import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Pill } from '~/components/pill';
import { formatDateTime } from '~/lib/format-date';
import { serverMessage } from '~/lib/api/server-error';
import { reportReasonKey } from '~/lib/reviews/reviews';
import { useResolveReviewReports, useReviewReports } from '~/hooks/use-reviews';

/**
 * What customers reported on a review (reason, comment, who, when) and the "mark resolved" action, once the admin acted
 * on the review (declined / hid / deleted it) or decided it is fine.
 */
export function ReviewReportsSection({ reviewId }: { reviewId: number }) {
	const { t } = useTranslation();
	const { data: reports, isLoading, isError, error } = useReviewReports(reviewId);
	const resolve = useResolveReviewReports();
	const open = (reports ?? []).filter(r => !r.is_resolved).length;

	const onResolve = () =>
		resolve.mutate(reviewId, {
			onSuccess: () => toast.success(t('catalog.reviews.reports.resolved')),
			onError: err => toast.error(serverMessage(err))
		});

	return (
		<section>
			<div className="mb-1 flex items-center justify-between gap-2">
				<h3 className="text-xs font-semibold tracking-[0.04em] text-foreground-subtle">{t('catalog.reviews.reports.title')}</h3>
				{open > 0 && (
					<Button size="sm" variant="outline" onClick={onResolve} disabled={resolve.isPending}>
						{t('catalog.reviews.reports.resolve')}
					</Button>
				)}
			</div>
			{isLoading ? (
				<Skeleton className="h-16 w-full rounded-md" />
			) : isError ? (
				<p className="text-sm text-foreground-muted">{serverMessage(error)}</p>
			) : !reports?.length ? (
				<p className="text-sm text-foreground-muted">{t('catalog.reviews.reports.none')}</p>
			) : (
				<ul className="flex flex-col divide-y divide-border-subtle rounded-md border border-border">
					{reports.map(r => (
						<li key={r.id} className="flex flex-col gap-1 px-3 py-2 text-sm">
							<div className="flex items-center justify-between gap-2">
								<span className="inline-flex items-center gap-1.5 font-medium text-foreground">
									<Flag size={13} className="text-danger-fg" />
									{t(reportReasonKey(r.reason))}
								</span>
								<Pill variant={r.is_resolved ? 'neutral' : 'warning'}>
									{r.is_resolved ? t('catalog.reviews.reports.stateResolved') : t('catalog.reviews.reports.stateOpen')}
								</Pill>
							</div>
							{r.comment && <p className="whitespace-pre-line text-foreground">{r.comment}</p>}
							<span className="text-[11px] text-foreground-muted">
								{[r.cust_nm, r.cust_eml].filter(Boolean).join(' · ') || `#${r.cust_id}`} · {formatDateTime(r.cdt)}
							</span>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
